import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  StandaloneSearchBox
} from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '@/config';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Search, Loader2, LocateFixed } from "lucide-react";

const containerStyle = {
  width: '100%',
  height: '400px',
  position: 'relative' as const
};

const defaultCenter = {
  lat: 12.9716,
  lng: 77.5946
};

const libraries: ("places" | "geometry" | "drawing" | "visualization")[] = ["places"];

interface MapAddressPickerProps {
  onAddressSelect: (address: {
    line1: string;
    line2: string;
    city: string;
    pincode: string;
    landmark: string;
    state?: string;
    lat?: number;
    lng?: number;
  }) => void;
}

export default function MapAddressPicker({ onAddressSelect }: MapAddressPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [address, setAddress] = useState("");
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const isManualUpdateRef = useRef(false);
  // The selected coordinate lives in a REF, not state, and <GoogleMap> is left
  // uncontrolled (defaultCenter + imperative panTo).
  // Previously `center` was state passed to <GoogleMap center={...}>, which made
  // the map controlled: every onIdle wrote to state -> React re-rendered ->
  // the map was re-centred -> onIdle fired again. That feedback loop is what made
  // the whole dialog flicker. Camera movement is now driven only by the user and
  // by explicit panTo() calls.
  const centerRef = useRef(defaultCenter);
  // Frozen first-render value handed to <GoogleMap center>. Never reassigned, so
  // the map is never re-centred by React.
  const initialCenterRef = useRef(defaultCenter);
  // Debounces reverse-geocoding so panning does not fire a request per frame.
  const geocodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Request location immediately when modal opens
  useEffect(() => {
    if (isOpen && isLoaded && map) {
      handleGetCurrentLocation();
    }
  }, [isOpen, isLoaded, map]);

  const sanitizeAddressText = useCallback((text: string) => {
    if (!text) return "";
    // 1. Normalize accents (e.g., 'é' -> 'e')
    const normalized = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    // 2. Remove anything that isn't Latin/Number/Punctuation
    return normalized.replace(/[^a-zA-Z0-9\s,.\-/#()]/g, "").replace(/\s+/g, " ").trim();
  }, []);

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    if (!window.google) return;

    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    // Debounced so a pan/zoom burst results in one lookup, not one per event.
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);

    setLoadingAddress(true);
    // Don't clear apiError here to avoid flashing

    geocodeTimerRef.current = setTimeout(() => {
      geocodeTimerRef.current = null;
      geocoderRef.current!.geocode({ location: { lat, lng } }, (results, status) => {
        setLoadingAddress(false);
        if (status === "OK" && results && results[0]) {
          const sanitized = sanitizeAddressText(results[0].formatted_address);
          setAddress(sanitized);
          setApiError(null);
        } else if (status === "ZERO_RESULTS") {
          setAddress("");
          setApiError("No address found for this exact location. Try moving the map slightly.");
        } else {
          const errorMsg = `Geocoding failed: ${status}`;
          console.error(errorMsg);
          setApiError(status === 'REQUEST_DENIED'
            ? "Geocoding API missing. Please enable it in Google Cloud Console."
            : errorMsg);
        }
      });
    }, 350);
  }, [sanitizeAddressText]);

  // Clear any pending geocode when the dialog closes/unmounts.
  useEffect(() => {
    return () => {
      if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    };
  }, []);

  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);

    // Try to get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        centerRef.current = pos;
        isManualUpdateRef.current = true;
        mapInstance.setCenter(pos);
        reverseGeocode(pos.lat, pos.lng);
      }, (error) => {
        console.warn("Initial geolocation failed:", error);
        reverseGeocode(defaultCenter.lat, defaultCenter.lng);
      });
    }
  }, [reverseGeocode]);

  // Drag no longer needs its own handler: handleIdle (which fires right after a
  // drag settles) does the same work. Keeping both meant two Geocoding calls per
  // pan and two competing setAddress() updates, which made the label flicker.

  const handleIdle = useCallback(() => {
    if (!map) return;
    // A programmatic pan (search pick / "my location") also fires onIdle. Skip it:
    // the coordinate and the address label were already set by that code path, and
    // re-resolving them here caused a second, conflicting geocode.
    if (isManualUpdateRef.current) {
      isManualUpdateRef.current = false;
      return;
    }
    const newCenter = map.getCenter();
    if (!newCenter) return;
    // Ref only — writing this to state re-rendered the map and re-triggered idle.
    centerRef.current = { lat: newCenter.lat(), lng: newCenter.lng() };
    // Keep the label in sync when the user pans/zooms by hand (debounced).
    reverseGeocode(newCenter.lat(), newCenter.lng());
  }, [map, reverseGeocode]);

  const onPlacesChanged = () => {
    if (searchBoxRef.current && map) {
      const places = searchBoxRef.current.getPlaces();
      if (places && places.length > 0) {
        const place = places[0];
        if (place.geometry && place.geometry.location) {
          const loc = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          centerRef.current = loc;
          isManualUpdateRef.current = true;
          map.panTo(loc);
          map.setZoom(17);
          setApiError(null);
          if (place.formatted_address) {
            const sanitized = sanitizeAddressText(place.formatted_address);
            setAddress(sanitized);
            setSearchQuery(sanitized);
          }
        }
      }
    }
  };

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setApiError("Geolocation is not supported by your browser.");
      return;
    }

    if (!map) return;

    setLoadingAddress(true);
    setApiError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        console.log("Browser reported position:", { lat, lng, accuracy: position.coords.accuracy });

        // Check if coordinates are suspiciously exactly 0,0 (often a sign of a mock location or sensor failure)
        if (lat === 0 && lng === 0) {
          setApiError("Browser returned 0,0 location. Please check your GPS/Sensors.");
          setLoadingAddress(false);
          return;
        }

        const pos = { lat, lng };
        centerRef.current = pos;
        // Let handleIdle resolve the address for this programmatic move — the
        // flag stays false so it is treated like a normal camera settle.
        isManualUpdateRef.current = false;
        map.setCenter(pos);
        map.setZoom(17);
        // Address will be fetched by handleIdle automatically
      },
      (error) => {
        console.error("Geolocation error:", error);
        let msg = "Could not retrieve location.";
        if (error.code === error.PERMISSION_DENIED) msg = "Location permission denied.";
        else if (error.code === error.POSITION_UNAVAILABLE) msg = "Location information unavailable.";
        else if (error.code === error.TIMEOUT) msg = "Location request timed out.";

        setApiError(msg);
        setLoadingAddress(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [map]);

  const handleConfirm = () => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: centerRef.current }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const fullAddress = results[0];
        const components = fullAddress.address_components;

        let street_number = "";
        let route = "";
        let premise = "";
        let subpremise = "";
        let landmark = "";
        let neighborhood = "";
        let sublocality_1 = "";
        let sublocality_2 = "";
        let locality = "";
        let city = "";
        let state = "";
        let pincode = "";

        components.forEach((comp, index) => {
          const types = comp.types || [];
          const val = sanitizeAddressText(comp.long_name);

          if (types.includes("street_number")) street_number = val;
          if (types.includes("route")) route = val;
          if (types.includes("premise")) premise = val;
          if (types.includes("subpremise")) subpremise = val;
          if (types.includes("landmark")) landmark = val;
          if (types.includes("neighborhood")) neighborhood = val;
          if (types.includes("sublocality_level_1")) sublocality_1 = val;
          if (types.includes("sublocality_level_2")) sublocality_2 = val;
          if (types.includes("locality")) locality = val;
          if (types.includes("administrative_area_level_2")) city = val;
          if (types.includes("administrative_area_level_1")) state = val;
          if (types.includes("postal_code")) pincode = val;

          // Fallback: If first component has no types but has a value, it's often the house number/ID
          if (index === 0 && types.length === 0 && !street_number) {
            street_number = val;
          }
        });

        const getWordCount = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

        // Build Line 1 aggressively to meet "3 words / 10 characters" validation
        const line1Parts = [street_number, subpremise, premise, route].filter(Boolean);
        let line1 = line1Parts.join(", ").trim();

        // Keep adding locally significant components until line1 is "valid"
        const extraLocalFields = [landmark, neighborhood, sublocality_2, sublocality_1];
        for (const field of extraLocalFields) {
          if (line1.length >= 10 && getWordCount(line1) >= 3) break;
          if (field && !line1.includes(field)) {
            line1 = [line1, field].filter(Boolean).join(", ").trim();
          }
        }

        // Final fallback if STILL too short: take multiple segments from formatted_address
        if (line1.length < 10 || getWordCount(line1) < 3) {
          const sanitizedFull = sanitizeAddressText(fullAddress.formatted_address);
          const segments = sanitizedFull.split(',');
          let combined = "";
          for (const seg of segments) {
            combined = [combined, seg.trim()].filter(Boolean).join(", ");
            if (combined.length >= 10 && getWordCount(combined) >= 3) break;
          }
          line1 = combined;
        }

        // Build Line 2: Combine remaining sublocalities that didn't go into line 1
        const line2Parts = [];
        if (sublocality_1 && !line1.includes(sublocality_1)) line2Parts.push(sublocality_1);
        if (sublocality_2 && !line1.includes(sublocality_2)) line2Parts.push(sublocality_2);
        if (neighborhood && !line1.includes(neighborhood)) line2Parts.push(neighborhood);
        const line2 = line2Parts.join(", ").trim();

        const finalCity = locality || city;

        console.log("Validation-Aware Parsing:", {
          original: fullAddress.formatted_address,
          parsed: { line1, line2, finalCity, state, pincode }
        });

        onAddressSelect({
          line1: line1,
          line2: line2,
          city: finalCity,
          pincode: pincode,
          landmark: "",
          state: state,
          lat: centerRef.current.lat,
          lng: centerRef.current.lng
        });
        setIsOpen(false);
      }
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div className="relative group cursor-pointer overflow-hidden rounded-lg border border-gray-200 transition-all hover:border-primary/50 hover:shadow-md w-full max-w-sm mx-auto map-trigger-preview">
          {/* Fake Map Background */}
          <div className="absolute inset-0 bg-[#f8f9fa] opacity-80" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Set Address From Map</span>
          </div>

          {/* Decorative grid pattern to simulate a map */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.03] map-grid-pattern"
          />
        </div>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[600px] p-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Google renders .pac-container as a direct child of <body>, i.e. OUTSIDE
        // this dialog. Radix therefore treats every tap on a prediction as an
        // "outside" interaction and, on touch devices, consumed the first tap to
        // dismiss/refocus — which is why everything needed a double tap. All three
        // handlers must ignore events originating in the predictions list.
        onPointerDownOutside={(e) => {
          if ((e.target as HTMLElement)?.closest?.(".pac-container")) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if ((e.target as HTMLElement)?.closest?.(".pac-container")) {
            e.preventDefault();
          }
        }}
        onFocusOutside={(e) => {
          if ((e.target as HTMLElement)?.closest?.(".pac-container")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="p-4 border-b">
          <DialogTitle>Select Your Location</DialogTitle>
        </DialogHeader>

        <div className="relative">
          {isLoaded ? (
            <>
              <div className="absolute top-4 left-4 right-4 z-10">
                {/*
                  StandaloneSearchBox binds Google's Autocomplete to its DIRECT
                  child, which MUST be a real <input>. The shadcn <Input> renders
                  its input inside a wrapper <div>, so the search box never bound
                  to anything and no predictions ever appeared — hence a plain
                  <input> here with the same styling applied manually.
                  text-base (16px) is required so iOS Safari does not auto-zoom
                  the page when the field is focused.
                */}
                <StandaloneSearchBox
                  onLoad={ref => searchBoxRef.current = ref}
                  onPlacesChanged={onPlacesChanged}
                >
                  <input
                    type="text"
                    placeholder="Search for area, street, or landmark"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                    className="h-11 w-full rounded-md border-none bg-white px-3 pr-10 text-base shadow-lg outline-none ring-offset-background focus-visible:ring-1 focus-visible:ring-ring"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onPlacesChanged();
                      }
                    }}
                  />
                </StandaloneSearchBox>
                <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>

              <GoogleMap
                mapContainerStyle={containerStyle}
                // Effectively uncontrolled: this is a constant, so React never
                // re-centres the map. All camera moves go through panTo/setCenter.
                // Passing a state-backed `center` here created a re-render ->
                // re-centre -> onIdle feedback loop that made the dialog flicker.
                center={initialCenterRef.current}
                zoom={15}
                onLoad={onMapLoad}
                onIdle={handleIdle}
                onDragStart={() => {
                  setApiError(null);
                  isManualUpdateRef.current = false;
                }}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  clickableIcons: false,
                  gestureHandling: 'greedy'
                }}
              >
                {/* Floating Current Location Button */}
                <div className="absolute top-20 right-2 z-10">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-10 w-10 rounded-md shadow-lg bg-white hover:bg-gray-100"
                    onClick={handleGetCurrentLocation}
                    title="Get Current Location"
                  >
                    <LocateFixed className="h-5 w-5 text-primary" />
                  </Button>
                </div>

                {/* Fixed Pin Overlay */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-20">
                  <div className="flex flex-col items-center">
                    <div className="bg-primary text-white p-2 rounded-full shadow-xl animate-bounce">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div className="w-1 h-3 bg-primary rounded-full -mt-1 shadow-lg" />
                  </div>
                </div>
              </GoogleMap>
            </>
          ) : (
            <div className="flex items-center justify-center bg-gray-50 h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
        </div>

        <div className="p-4 bg-white border-t">
          <div className="flex items-start gap-3 mb-4">
            <div className={apiError ? "bg-red-100 p-2 rounded-full" : "bg-orange-100 p-2 rounded-full"}>
              <MapPin className={apiError ? "h-5 w-5 text-red-600" : "h-5 w-5 text-orange-600"} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">
                {apiError ? "API Error" : "Select Location"}
              </p>
              <p className={`text-sm font-medium line-clamp-2 ${apiError ? "text-red-600" : "text-gray-900"}`}>
                {apiError
                  ? apiError
                  : loadingAddress
                    ? "Fetching address..."
                    : address || "Move map to select location"
                }
              </p>
            </div>
          </div>
          {loadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
              <strong>Map Load Error:</strong> {loadError.message}
              <br />
              Ensure billing is enabled and API key is correct.
            </div>
          )}
          <Button
            type="button"
            className="w-full h-12 text-base font-bold uppercase tracking-wide active:scale-[0.98] transition-transform"
            onClick={handleConfirm}
            disabled={loadingAddress || (!!apiError && !address)}
          >
            Confirm Location
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
